import { memo } from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import type { DocumentReference, Patient } from "fhir/r5";
import {
  DocumentsChecklist,
  type DocumentCategory,
} from "src-next/modules/ecommerce";

interface DocumentsSectionProps {
  categories: DocumentCategory[];
  organizationId?: string;
  practitionerRoleId?: string;
  transactionId?: string;
  patient: Patient;
  interactionDisabled: boolean;
  error: boolean;
  forbidden: boolean;
  missingCpf: boolean;
  onRefetch: () => void;
  onSendWhatsApp?: (
    patients: Patient[],
    options?: { onSent?: () => void },
  ) => void;
  onView: (doc: DocumentReference, title: string) => void;
}

export const DocumentsSection = memo(function DocumentsSection({
  categories,
  organizationId,
  practitionerRoleId,
  transactionId,
  patient,
  interactionDisabled,
  error,
  forbidden,
  missingCpf,
  onRefetch,
  onSendWhatsApp,
  onView,
}: DocumentsSectionProps) {
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ flexShrink: 0, mb: 1 }}
      >
        Verifique todos os documentos para liberar a compra.
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{ flexShrink: 0, mb: 1.5 }}
          action={
            <Button color="inherit" size="small" onClick={onRefetch}>
              Tentar novamente
            </Button>
          }
        >
          Não foi possível carregar os documentos.
        </Alert>
      )}
      {forbidden && (
        <Alert severity="warning" sx={{ flexShrink: 0, mb: 1.5 }}>
          Sem permissão para ver os documentos deste paciente.
        </Alert>
      )}
      {missingCpf && (
        <Alert severity="warning" sx={{ flexShrink: 0, mb: 1.5 }}>
          CPF indisponível para buscar documentos.
        </Alert>
      )}

      {!error && !forbidden && !missingCpf && (
        <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
          <DocumentsChecklist
            categories={categories}
            organizationId={organizationId}
            practitionerRoleId={practitionerRoleId}
            transactionId={transactionId}
            patient={patient}
            interactionDisabled={interactionDisabled}
            onSendWhatsApp={onSendWhatsApp}
            onView={onView}
            onDocumentsChanged={onRefetch}
          />
        </Box>
      )}
    </Box>
  );
});
